import { useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface SwipeConfig {
  threshold?: number;
  preventScrollThreshold?: number;
}

export const useSwipeNavigation = (config: SwipeConfig = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    threshold = 100,
    preventScrollThreshold: _preventScrollThreshold = 20,
  } = config;

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchEndRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);

  // Define the navigation order
  const getOrderedRoutes = () => {
    return [
      "/", // Landing
      "/creators-choice", // Creators Choice
      "/radio", // Radio
      "/podcasts", // Pods
    ];
  };

  const getNextRoute = (currentPath: string, direction: "left" | "right") => {
    const routes = getOrderedRoutes();

    // Normalize current path to match routes list (handle sub-paths if necessary or strict match)
    // For simplicity, we match the exact start for now, or strict content.
    // The user listed: landing - creators choice - radio - pods.

    let currentIndex = routes.findIndex((r) => r === currentPath);

    // Fallback: If we are in a sub-route (e.g. /radio/station-slug), maybe we don't want to swipe away?
    // Or we strictly only swipe on the main pages.
    // User said: "change between all pages landingpage - creators choice - radio - pods"
    // implies these are top level.

    if (currentIndex === -1) {
      // Handle equivalent paths
      if (currentPath === "/pods")
        currentIndex = 3; // /podcasts
      else return null; // Don't swipe if not on a main tab
    }

    if (direction === "left") {
      // Next page
      if (currentIndex < routes.length - 1) return routes[currentIndex + 1];
    } else {
      // Prev page
      if (currentIndex > 0) return routes[currentIndex - 1];
    }

    return null;
  };

  const handleSwipe = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;

    const distanceX = touchStartRef.current.x - touchEndRef.current.x;
    const distanceY = touchStartRef.current.y - touchEndRef.current.y;
    const isLeftSwipe = distanceX > threshold;
    const isRightSwipe = distanceX < -threshold;

    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY); // Minimal vertical movement check

    if (isHorizontalSwipe) {
      const mainContent = document.getElementById("main-content");

      if (isLeftSwipe) {
        const next = getNextRoute(location.pathname, "left");
        if (next) {
          // Apply slide-out animation
          if (mainContent) {
            mainContent.classList.add("page-slide-out-left");
          }
          // Wait for animation, then navigate
          setTimeout(() => {
            navigate(next);
            // Remove old animation, add slide-in
            if (mainContent) {
              mainContent.classList.remove("page-slide-out-left");
              mainContent.classList.add("page-slide-in-right");
              // Clean up after animation
              setTimeout(
                () => mainContent.classList.remove("page-slide-in-right"),
                250,
              );
            }
          }, 200);
        }
      } else if (isRightSwipe) {
        const next = getNextRoute(location.pathname, "right");
        if (next) {
          // Apply slide-out animation
          if (mainContent) {
            mainContent.classList.add("page-slide-out-right");
          }
          // Wait for animation, then navigate
          setTimeout(() => {
            navigate(next);
            // Remove old animation, add slide-in
            if (mainContent) {
              mainContent.classList.remove("page-slide-out-right");
              mainContent.classList.add("page-slide-in-left");
              // Clean up after animation
              setTimeout(
                () => mainContent.classList.remove("page-slide-in-left"),
                250,
              );
            }
          }, 200);
        }
      }
    }

    // Reset
    touchStartRef.current = null;
    touchEndRef.current = null;
    isDraggingRef.current = false;
  };

  const onTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    // Check if target is interactive to avoid conflict
    const target = e.target as HTMLElement;
    if (
      target.closest('button, a, input, textarea, [role="button"], .no-swipe')
    )
      return;

    // Also dispatch for carousels: often they stop propagation.
    // If we attach to top level, we get it.
    // BUT we must filter out if the user is scrolling a carousel.
    // Usually carousels handle their own touch events and might stop propagation.
    // If they strictly want "empty area", the closest check above helps.

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

    touchStartRef.current = { x: clientX, y: clientY };
    touchEndRef.current = { x: clientX, y: clientY }; // Initialize end too
    isDraggingRef.current = true;
  };

  const onTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDraggingRef.current || !touchStartRef.current) return;

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

    touchEndRef.current = { x: clientX, y: clientY };
  };

  const onTouchEnd = () => {
    if (!isDraggingRef.current) return;
    handleSwipe();
  };

  const onMouseLeave = () => {
    if (isDraggingRef.current) handleSwipe();
  };

  // Combine handlers for spread
  const handlers = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMouseDown: onTouchStart,
    onMouseMove: onTouchMove,
    onMouseUp: onTouchEnd,
    onMouseLeave,
  };

  return handlers;
};
